// Global Instructions Rule Applied!
//
// Company Documents — reads `view__account_documents` and writes the type
// classification. Files here arrive two ways: a person uploading through the
// tab, and the internal-agents follower ingesting PDF attachments off monitored
// mailboxes (design §D2). Classification is the human correction path for what
// the agent set at ingest.
//
// Responses are Zod-validated before use (CLAUDE.md). The file rows stay
// permissive rather than `.strict()`: the RPC's row shape is still growing with
// the D2 work, and a new key must not blank the tab.
import { z } from 'zod'
import { buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { API_ROUTES } from '../../../core/utils/endpoints.ts'
import { validateWithZodSchema } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const DocumentCategorySchema = z.object({
  id: z.string(),
  label: z.string()
})

const DocumentTypeSchema = z.object({
  id: z.string(),
  label: z.string(),
  category_id: z.string().nullish(),
  category_label: z.string().nullish()
})

const AccountDocumentsSchema = z.object({
  document_categories: z.array(DocumentCategorySchema).default([]),
  document_types: z.array(DocumentTypeSchema).default([]),
  files: z.array(z.record(z.string(), z.unknown())).default([])
})

export type AccountDocumentsData = z.infer<typeof AccountDocumentsSchema>

export interface FetchAccountDocumentsResponse {
  data: AccountDocumentsData
  error?: string
}

const EMPTY: AccountDocumentsData = {
  document_categories: [],
  document_types: [],
  files: []
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export async function fetchAccountDocuments(
  accountId: string,
  accessToken: string | null
): Promise<FetchAccountDocumentsResponse> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.DOCUMENTS, { id: accountId }),
      { method: API_ROUTES.COMPANIES.DOCUMENTS.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const errData = await response.json().catch(() => ({}))
      throw new Error((errData as { message?: string }).message || response.statusText)
    }
    const json = (await response.json()) as { data?: unknown }
    const validated = validateWithZodSchema(
      AccountDocumentsSchema, json.data ?? EMPTY, 'account documents'
    ) as AccountDocumentsData
    return { data: validated }
  } catch (e) {
    console.error('Error fetching account documents:', e)
    return { data: EMPTY, error: e instanceof Error ? e.message : 'Failed to fetch documents' }
  }
}

export async function classifyAccountFile(
  fileId: string,
  documentTypeId: string | null,
  accessToken: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.DOCUMENTS_CLASSIFY),
      {
        method: API_ROUTES.COMPANIES.DOCUMENTS_CLASSIFY.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { file_id: fileId, document_type_id: documentTypeId } })
      }
    )
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      throw new Error((errData as { error?: string }).error || response.statusText)
    }
    return { success: true }
  } catch (e) {
    console.error('Error classifying account file:', e)
    return { success: false, error: e instanceof Error ? e.message : 'Failed to classify file' }
  }
}
